import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";

/** One slice: a category, or the fold of everything past the fourth. */
export interface Slice {
  id: string;
  name: string;
  /** A positive magnitude. */
  amountMinor: number;
  /** Basis points of the whole: 2929 is 29.29%. */
  shareBp: number;
}

/**
 * How many slices get a step of the ramp before the rest are folded together.
 *
 * **Four, and the number is measured rather than chosen.** The ring is drawn in
 * one hue because `project.md` §5.5 gives this product a single accent colour,
 * and a sequential ramp has a hard limit on how many steps stay tellable apart.
 * Running the `dataviz` skill's validator over blue ramps puts the worst
 * adjacent pair of six steps at ΔE 5.8 and of five steps at 13.9 — both under
 * the floor of 15, which is where two colours stop being distinguishable to
 * full-colour vision, a floor that direct labels explicitly do not excuse. Four
 * steps passes at 18.9.
 *
 * Everything past the fourth is one neutral slice, and stays legible in the
 * table underneath.
 */
const RAMP_STEPS = 4;

/**
 * The ramp, dark to pale, plus the neutral the fold is drawn in (ADR 0006).
 *
 * Each step is a single theme token: the light/dark split that used to sit on
 * the class itself now lives inside the token (see `src/theme/themes.css`),
 * so the class is the same in both schemes and the theme decides what colour
 * it resolves to.
 */
const RAMP = ["stroke-share-1", "stroke-share-2", "stroke-share-3", "stroke-share-4"];
const OTHER = "stroke-share-other";

const SWATCH = ["bg-share-1", "bg-share-2", "bg-share-3", "bg-share-4"];
const OTHER_SWATCH = "bg-share-other";

/**
 * The gap between two slices, in hundredths of the ring.
 *
 * The pale end of the ramp is under 3:1 against the page, so two slices meeting
 * edge to edge would read as one longer slice. The gap is the boundary, drawn in
 * the page's own colour rather than in ink.
 */
const GAP = 0.4;

/** Below this a slice would be thinner than its own gap and vanish. */
const MIN_VISIBLE = 0.8;

/**
 * Where a month's money went, as a ring (ADR 0006).
 *
 * Superseding ADR 0003 §7's flat ban on donuts is what allows this to exist, and
 * the rules that ban rested on all still hold here:
 *
 * - **The ring is an ornament on top of a legible answer.** Every figure is in
 *   the DOM as text in the list beside it. Delete the `<svg>` and the screen
 *   still answers the question — which is the test ADR 0003 rule 5 asks to be
 *   applied literally.
 * - **Nothing is red.** Red means overspend and only overspend (§5.5), and every
 *   month has spending in it.
 * - **Colour is confirmation, never the carrier.** The slices are ordered
 *   largest first and every one is named and numbered beside its swatch, so the
 *   ring reads identically in greyscale, in print, and to a reader who cannot
 *   separate two steps of the ramp at all.
 * - **Light and dark are both first-class**, from two separately validated
 *   ramps rather than one flipped: the light ramp's darkest step sits at 1.19:1
 *   against the dark theme's canvas and would be invisible there.
 */
export function ShareDonut({
  slices,
  currency,
  totalMinor,
  totalLabel,
  emptyLabel,
}: {
  slices: Slice[];
  currency: string;
  totalMinor: number;
  totalLabel: string;
  emptyLabel: string;
}) {
  const t = useT();
  const format = useFormat();

  const drawn = fold(slices, t("report.otherCategories"));

  if (drawn.length === 0 || totalMinor <= 0) {
    return <p className="text-sm text-muted">{emptyLabel}</p>;
  }

  // Each slice's start, walked round the ring. The offsets come from the shares
  // the server apportioned, which sum to exactly 10000 — so the last slice ends
  // where the first began instead of a fraction short of it.
  let cursor = 0;
  const arcs = drawn.map((slice, index) => {
    const length = slice.shareBp / 100;
    const start = cursor;
    cursor += length;
    return { slice, index, start, length };
  });

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8">
      <div className="relative shrink-0">
        <svg
          viewBox="0 0 100 100"
          className="h-44 w-44 -rotate-90"
          role="img"
          aria-label={t("report.ringLabel", {
            total: format.amount(totalMinor, currency),
            n: String(drawn.length),
          })}
        >
          {arcs.map(({ slice, index, start, length }) => {
            // The gap is taken off the slice rather than added between them, so
            // the ring's total turn stays exactly one turn.
            const drawnLength = Math.max(length - GAP, MIN_VISIBLE);
            return (
              <circle
                key={slice.id}
                cx="50"
                cy="50"
                r="40"
                fill="none"
                strokeWidth="14"
                pathLength={100}
                strokeDasharray={`${drawnLength} ${100 - drawnLength}`}
                strokeDashoffset={-start}
                className={index < RAMP_STEPS ? RAMP[index] : OTHER}
                data-testid="donut-slice"
              />
            );
          })}
        </svg>
        {/* The one number the ring itself is bad at carrying. */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xs text-muted">{totalLabel}</span>
          <span className="num text-lg font-semibold text-ink" data-testid="donut-total">
            {format.amount(totalMinor, currency)}
          </span>
        </div>
      </div>

      {/* The answer in text. This list is the chart; the ring is the ornament. */}
      <ul className="w-full space-y-1.5">
        {drawn.map((slice, index) => (
          <li
            key={slice.id}
            className="flex items-baseline gap-2 text-sm"
            data-testid="donut-legend-row"
          >
            <span
              className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-sm ${
                index < RAMP_STEPS ? SWATCH[index] : OTHER_SWATCH
              }`}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate">{slice.name}</span>
            <span className="num tabular-nums text-muted">
              {format.share(slice.shareBp)}
            </span>
            <span className="num w-24 text-right tabular-nums">
              {format.amount(slice.amountMinor, currency)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Sorts the slices and folds everything past the ramp into one.
 *
 * The fold happens here rather than at the call site so that no screen can hand
 * the ring more steps than the ramp has: a fifth hue does not exist, and
 * inventing one is the mistake the whole design is arranged to prevent.
 */
function fold(slices: Slice[], otherName: string): Slice[] {
  const ranked = [...slices]
    .filter((slice) => slice.amountMinor > 0)
    .sort((a, b) =>
      b.amountMinor !== a.amountMinor
        ? b.amountMinor - a.amountMinor
        : a.name.localeCompare(b.name),
    );

  if (ranked.length <= RAMP_STEPS + 1) {
    return ranked;
  }

  const kept = ranked.slice(0, RAMP_STEPS);
  const rest = ranked.slice(RAMP_STEPS);
  return [
    ...kept,
    {
      id: "__other__",
      name: otherName,
      amountMinor: rest.reduce((sum, slice) => sum + slice.amountMinor, 0),
      shareBp: rest.reduce((sum, slice) => sum + slice.shareBp, 0),
    },
  ];
}
